import React from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { CheckIcon, UsersIcon, LightbulbIcon, GlobeIcon, BookOpenIcon, AwardIcon, BriefcaseIcon } from 'lucide-react';
export function Solutions() {
  const solutions = [{
    title: 'Meetings & Retreats',
    description: 'Strategic gatherings that spark innovation and alignment while providing a refreshing change of environment.',
    outcomes: ['Increased team alignment', 'Accelerated decision making', 'Enhanced collaboration', 'Improved employee engagement'],
    activities: ['Strategy sprints', 'Executive cabanas', 'Beachside breakouts', 'Sunrise wellness sessions'],
    image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Incentives',
    description: 'Reward top performers with unforgettable experiences that inspire future success and drive motivation.',
    outcomes: ['Boosted team morale', 'Increased retention', 'Motivation for future performance', 'Strengthened company culture'],
    activities: ['Luxury excursions', 'VIP experiences', 'Gala dinners', 'Adventure activities'],
    image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Brand Launches',
    description: 'Create buzz and alignment around your new product or initiative with immersive brand experiences.',
    outcomes: ['Consistent messaging', 'Enthusiastic brand advocates', 'Memorable product experiences', 'Media-worthy moments'],
    activities: ['Immersive product demos', 'Media moments', 'Training workshops', 'Brand activation experiences'],
    image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Hybrid/Virtual Enhancements',
    description: 'Extend your in-person event to remote participants with engaging digital experiences.',
    outcomes: ['Inclusive participation', 'Extended reach', 'Content longevity', 'Cost-effective engagement'],
    activities: ['Livestreams', 'Virtual chat', 'Interactive polling', 'Digital breakout rooms'],
    image: 'https://images.unsplash.com/photo-1609921212029-bb5a28e60960?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }];
  // New expert trainers data
  const expertCategories = [{
    title: 'Leadership Development',
    icon: <AwardIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2832" />,
    description: 'Transform managers into inspiring leaders through immersive workshops and personalized coaching.',
    experts: ['Harvard Business School', 'Wharton', 'Stanford GSB']
  }, {
    title: 'Innovation & Design Thinking',
    icon: <LightbulbIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2833" />,
    description: 'Unlock creative problem-solving with hands-on workshops led by innovation experts.',
    experts: ['IDEO Alumni', 'MIT Media Lab', 'Stanford d.school']
  }, {
    title: 'Strategic Planning',
    icon: <BriefcaseIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2834" />,
    description: 'Develop actionable roadmaps with guidance from experienced strategy consultants and academics.',
    experts: ['McKinsey Alumni', 'London Business School', 'INSEAD']
  }, {
    title: 'Team Effectiveness',
    icon: <UsersIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2835" />,
    description: 'Build high-performing teams through evidence-based frameworks and experiential learning.',
    experts: ['Google re:Work Trainers', 'Gallup Certified Coaches', 'Kellogg School']
  }];
  const services = [{
    title: 'Travel Logistics',
    items: ['Room blocks', 'Flight coordination', 'Ground transportation', 'VIP arrivals & departures']
  }, {
    title: 'Event Curation & Design',
    items: ['Custom agendas', 'Brand storytelling', 'Expert trainers', 'Theme development']
  }, {
    title: 'Production & Logistics',
    items: ['Venue consulting', 'Vendor management', 'On-site team', 'Risk management']
  }, {
    title: 'Event Tech',
    items: ['Custom apps', 'AV solutions', 'QR experiences', 'Virtual integrations']
  }, {
    title: 'Wellness & Community',
    items: ['Fitness activities', 'CSR programs', 'Beach clean-ups', 'Reef restoration']
  }];
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-2836">
      {/* Hero */}
      <section className="bg-[#0099A1] py-20" data-id="element-2837">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2838">
          <div className="max-w-3xl" data-id="element-2839">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-2840">
              Solutions
            </h1>
            <p className="text-xl text-white/90" data-id="element-2841">
              End-to-end event management tailored to your organization's unique
              goals and culture.
            </p>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-16 bg-white" data-id="element-2842">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2843">
          <div className="space-y-24" data-id="element-2844">
            {solutions.map((solution, index) => <div key={index} className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`} data-id="element-2845">
                <div className={index % 2 === 1 ? 'md:col-start-2' : ''} data-id="element-2846">
                  <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-2847">
                    {solution.title}
                  </h2>
                  <p className="text-lg text-[#374047]/80 mb-8" data-id="element-2848">
                    {solution.description}
                  </p>
                  <div className="mb-6" data-id="element-2849">
                    <h3 className="text-xl font-semibold text-[#0099A1] mb-4" data-id="element-2850">
                      Outcomes
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2851">
                      {solution.outcomes.map((outcome, i) => <li key={i} className="flex items-start" data-id="element-2852">
                          <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2853" />
                          <span className="ml-3 text-[#374047]" data-id="element-2854">{outcome}</span>
                        </li>)}
                    </ul>
                  </div>
                  <div data-id="element-2855">
                    <h3 className="text-xl font-semibold text-[#0099A1] mb-4" data-id="element-2856">
                      Typical Activities
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2857">
                      {solution.activities.map((activity, i) => <li key={i} className="flex items-start" data-id="element-2858">
                          <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2859" />
                          <span className="ml-3 text-[#374047]" data-id="element-2860">
                            {activity}
                          </span>
                        </li>)}
                    </ul>
                  </div>
                  <div className="mt-8" data-id="element-2861">
                    <Button variant="outline" to="/contact" data-id="element-2862">
                      Discuss Your {solution.title}
                    </Button>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'md:col-start-1' : ''} data-id="element-2863">
                  <div className="relative h-[400px] rounded-xl overflow-hidden shadow-lg" data-id="element-2864">
                    <img src={solution.image} alt={solution.title} className="w-full h-full object-cover" data-id="element-2865" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-2866"></div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Expert Trainers Section - NEW */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-2867">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2868">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2869">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-2870">
              World-Class Expert Network
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-2871">
              Elevate your corporate retreat with specialized training from top
              business school professors and industry experts.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16" data-id="element-2872">
            <div data-id="element-2873">
              <h3 className="text-2xl font-bold text-[#374047] mb-4" data-id="element-2874">
                From Boardroom to Classroom to Beach
              </h3>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-2875">
                Our exclusive network of professors from the world's top
                business schools and corporate trainers brings unparalleled
                expertise to your retreat experience.
              </p>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-2876">
                We carefully match expert facilitators to your specific
                organizational goals, creating custom learning experiences that
                drive real business outcomes.
              </p>
              <div className="space-y-4" data-id="element-2877">
                <div className="flex items-start" data-id="element-2878">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2879" />
                  <span className="ml-3 text-[#374047]" data-id="element-2880">
                    Expert-led workshops integrated into your retreat agenda
                  </span>
                </div>
                <div className="flex items-start" data-id="element-2881">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2882" />
                  <span className="ml-3 text-[#374047]" data-id="element-2883">
                    Immersive learning experiences in inspiring settings
                  </span>
                </div>
                <div className="flex items-start" data-id="element-2884">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2885" />
                  <span className="ml-3 text-[#374047]" data-id="element-2886">
                    Custom content development aligned with your objectives
                  </span>
                </div>
                <div className="flex items-start" data-id="element-2887">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2888" />
                  <span className="ml-3 text-[#374047]" data-id="element-2889">
                    Follow-up implementation support for lasting impact
                  </span>
                </div>
              </div>
            </div>
            <div data-id="element-2890">
              <img src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Expert-led workshop" className="rounded-xl shadow-lg" data-id="element-2891" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2892">
            {expertCategories.map((category, index) => <Card key={index} className="h-full" data-id="element-2893">
                <CardBody className="p-6" data-id="element-2894">
                  <div className="flex flex-col items-center text-center mb-6" data-id="element-2895">
                    <div className="mb-4 mt-2" data-id="element-2896">{category.icon}</div>
                    <h3 className="text-xl font-bold text-[#374047]" data-id="element-2897">
                      {category.title}
                    </h3>
                  </div>
                  <p className="text-[#374047]/80 mb-6" data-id="element-2898">
                    {category.description}
                  </p>
                  <div className="mt-auto" data-id="element-2899">
                    <h4 className="text-sm font-semibold text-[#0099A1] mb-2" data-id="element-2900">
                      Featured Experts From:
                    </h4>
                    <ul className="space-y-1" data-id="element-2901">
                      {category.experts.map((expert, i) => <li key={i} className="text-sm text-[#374047]" data-id="element-2902">
                          {expert}
                        </li>)}
                    </ul>
                  </div>
                </CardBody>
              </Card>)}
          </div>
          <div className="mt-12 text-center" data-id="element-2903">
            <Button variant="primary" to="/contact" data-id="element-2904">
              <BookOpenIcon className="h-5 w-5 mr-2" data-id="element-2905" />
              Explore Expert-Led Programs
            </Button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-[#F4E8D0]/50" data-id="element-2906">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2907">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2908">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-2909">
              Included Services
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-2910">
              Every DestaMeet experience includes comprehensive support from our
              expert team.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2911">
            {services.map((service, index) => <Card key={index} className="h-full" data-id="element-2912">
                <CardBody data-id="element-2913">
                  <h3 className="text-xl font-bold text-[#0099A1] mb-4" data-id="element-2914">
                    {service.title}
                  </h3>
                  <ul className="space-y-3" data-id="element-2915">
                    {service.items.map((item, i) => <li key={i} className="flex items-start" data-id="element-2916">
                        <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2917" />
                        <span className="ml-3 text-[#374047]" data-id="element-2918">{item}</span>
                      </li>)}
                  </ul>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>

      <CtaSection data-id="element-2919" />
    </div>;
}